import type { AGUCrumbItem, AGUIconName } from 'agu_app_shell';
import type {
    CvTagCategoryName, ResearchCareerMatches, ResearchCvReviewStatus, ResearchCvSegment, ResearchCvTag, ResearchJobProfile, ResearchMatchedRequirement,
    ResearchMemberCv, ResearchProfileShare, ResearchRequirementRef, ResearchRoleMatch,
} from '../../models/research.models';
import { RA_MODULE_KEYS } from '../../ra-module-keys';

/**
 * The pure state behind the Career Explorer pages (P/06_career-explorer.html): which reading step a CV is on, what to say about a
 * CV that was saved but not processed, how review tags group into cards, and the numbers/crumbs the pages print. No Angular, no I/O.
 */

/** The client-side refusal when the consent box is not ticked (spec §F4; P6:1585 is the checkbox it gates). */
export const CONSENT_REQUIRED_COPY = 'Please confirm you understand how your CV is used.';

/** Where a role's Job Finder listings are: not asked for yet (waiting on the profile), in flight, failed for real, or in. */
export type OpeningsState = 'idle' | 'loading' | 'failed' | 'done';

export type CareerExplorerView = 'start' | 'ready' | 'paths' | 'role' | 'review';

// ── Reading ────────────────────────────────────────────────────────────

export const READING_STEPS = ['Reading your CV', 'Tagging education and skills', 'Matching to career paths'] as const;

/** The reading step to show: `submitting`/`processing` with no tags is 0, `processing` with tags 1, `done` while matches load 2; otherwise none. */
export function ReadingStep(cv: ResearchMemberCv, matchesInFlight: boolean): 0 | 1 | 2 | null {
    if (cv.parseState === 'submitting') return 0;
    if (cv.parseState === 'processing') return cv.tags.length === 0 ? 0 : 1;
    if (cv.parseState === 'done' && matchesInFlight) return 2;
    return null;
}

/**
 * How long after a successful upload a `not_submitted` CV is still treated as "the parse has not started yet" (H-2 d): the server
 * starts the parse fire-and-forget after finalize, so the page's first read can land before the claim.
 */
export const CV_SUBMIT_GRACE_MS = 60_000;

/** True while a `not_submitted` CV is inside the post-upload grace window (`graceUntil` is the upload time + `CV_SUBMIT_GRACE_MS`). */
export function AwaitingSubmit(cv: ResearchMemberCv, graceUntil: number | null, now: number): boolean {
    return cv.parseState === 'not_submitted' && graceUntil !== null && now < graceUntil;
}

export interface NotProcessedMessage {
    readonly Title: string;
    readonly Body: string;
}

const UNAVAILABLE_COPY: NotProcessedMessage = {
    Title: 'Saved. Career paths are not available for this file yet.',
    Body: 'PDF for career paths; DOCX and TXT are saved for job matching only.',
};
const COMING_SOON_COPY: NotProcessedMessage = {
    Title: 'Saved. Career paths are coming to this environment soon.',
    Body: 'The file is saved for job matching.',
};

/** The "saved, not yet processed" panel for a CV that never reached the provider; null for every state with its own panel. */
export function NotProcessedCopy(cv: ResearchMemberCv): NotProcessedMessage | null {
    switch (cv.parseState) {
        case 'not_submitted':
        case 'awaiting_provider_support':
        case 'not_parseable':
        case 'unsupported_format': // a format rejection is a typed outcome, not a failure (ra-cv-intake-and-parsing-design.md:111)
            return UNAVAILABLE_COPY;
        case 'failed':
            return cv.errorCode === 'refused_prod_guard' ? COMING_SOON_COPY : null;
        default:
            return null;
    }
}

/** The ready page's calm panel when the provider cannot match roles (`parse_only`): never an error. */
export interface ParseOnlyMessage {
    readonly Icon: AGUIconName;
    readonly Title: string;
    readonly Body: string;
    /** The action into review; null when the CV has no tags to review. */
    readonly ReviewLabel: string | null;
}

const REVIEW_WITH_CV_LABEL = 'Review with your CV →';

/** Matching is not switched on yet (the brief's copy, P6's .ps-panel look). */
export const PARSE_ONLY_COPY: ParseOnlyMessage = {
    Icon: 'spark',
    Title: 'Your career paths are on their way',
    Body: 'Your CV is read and your tags are ready to review. Career paths appear here as soon as our matching partner switches them on.',
    ReviewLabel: REVIEW_WITH_CV_LABEL,
};

/** `rereadBlocked`: the provider forgot the CV and both submit switches are off. The not-processed family: one calm line, no blame. */
export const REREAD_BLOCKED_COPY: ParseOnlyMessage = {
    Icon: 'file',
    Title: 'Your CV is saved. Career paths will be read again when CV reading is switched on.',
    Body: '',
    ReviewLabel: REVIEW_WITH_CV_LABEL,
};

/** `REREAD_BLOCKED_COPY` for a CV with no tags: nothing to review, so no action. */
export const REREAD_BLOCKED_NO_TAGS_COPY: ParseOnlyMessage = { ...REREAD_BLOCKED_COPY, ReviewLabel: null };

/** The parse-only panel for these matches; one of the constants above, so the template binding is stable. */
export function ParseOnlyCopy(rereadBlocked: boolean, tagCount: number): ParseOnlyMessage {
    if (!rereadBlocked) return PARSE_ONLY_COPY;
    return tagCount > 0 ? REREAD_BLOCKED_COPY : REREAD_BLOCKED_NO_TAGS_COPY;
}

// ── Review ─────────────────────────────────────────────────────────────

/** P/06_career-explorer.html:1319-1325 (`cert` there is the model's `certification`). */
export const ReviewCategories = ['skill', 'education', 'license', 'certification', 'title'] as const;
export type ReviewCategory = typeof ReviewCategories[number];

export const ReviewCategoryLabels: Readonly<Record<ReviewCategory, string>> = {
    skill: 'Skills', education: 'Education', license: 'Licenses', certification: 'Certifications', title: 'From your job titles',
};
/** The singular form the review crumb ends with (`one` in P/06_career-explorer.html:1319-1325, used at :1711). */
export const ReviewCategoryNouns: Readonly<Record<ReviewCategory, string>> = {
    skill: 'Skill', education: 'Education', license: 'License', certification: 'Certification', title: 'Job title',
};

export interface ReviewQuote {
    readonly text: string;
    readonly page: number | null;
    /** The segment the quote is: the review viewer highlights exactly these, in quote order (H-14). */
    readonly segmentRef: string;
}

export interface ReviewCard {
    providerTagID: string;
    name: string;
    category: CvTagCategoryName;
    confidence: 'implied' | 'plausible';
    reviewStatus: ResearchCvReviewStatus;
    quotes: ReadonlyArray<ReviewQuote>;
    chainMembers: readonly string[];
}

function QuoteOf(tag: ResearchCvTag, segments: readonly ResearchCvSegment[]): ReviewQuote | null {
    const segment = segments.find(s => s.segmentRef === tag.segmentRef);
    return segment ? { text: segment.text, page: segment.locations[0]?.page ?? null, segmentRef: segment.segmentRef } : null;
}

/** The tags the member reviews (not `stated`, not `asserted`), one card per concept with every quote, chains collapsed to position 0. */
export function GroupReviewTags(tags: readonly ResearchCvTag[], segments: readonly ResearchCvSegment[]): ReviewCard[] {
    const cards = new Map<string, ReviewCard>();
    for (const tag of tags) {
        if (tag.confidence === 'stated' || tag.confidence === 'asserted' || tag.chainPosition !== 0) continue;
        let card = cards.get(tag.providerTagID);
        if (!card) {
            card = {
                providerTagID: tag.providerTagID, name: tag.name ?? tag.providerTagID, category: tag.category,
                confidence: tag.confidence, reviewStatus: tag.reviewStatus, quotes: [], chainMembers: [],
            };
            cards.set(tag.providerTagID, card);
        }
        const quote = QuoteOf(tag, segments);
        if (quote && !card.quotes.some(q => q.text === quote.text && q.page === quote.page)) card.quotes = [...card.quotes, quote];
        const members = tags
            .filter(t => t.segmentRef === tag.segmentRef && t.chainRef === tag.chainRef && t.chainPosition !== 0)
            .map(t => t.providerTagID)
            .filter(id => id !== tag.providerTagID && !card.chainMembers.includes(id));
        if (members.length > 0) card.chainMembers = [...card.chainMembers, ...members];
    }
    return [...cards.values()];
}

/** How many cards in each category still wait for the member (the review tabs' "{n} pending"). */
export function PendingCounts(cards: readonly ReviewCard[]): Record<ReviewCategory, number> {
    const counts: Record<ReviewCategory, number> = { skill: 0, education: 0, license: 0, certification: 0, title: 0 };
    for (const c of cards) {
        if (c.reviewStatus === 'unreviewed' && (ReviewCategories as readonly string[]).includes(c.category)) counts[c.category as ReviewCategory] += 1;
    }
    return counts;
}

/** Distinct tags the CV states outright: the review view's static "Stated in your CV" tab (P6:556). */
export function StatedCount(tags: readonly ResearchCvTag[]): number {
    return new Set(tags.filter(t => t.confidence === 'stated').map(t => t.providerTagID)).size;
}

/** Distinct tags and how many are confirmed (`stated`, approved, or member-asserted): `ConfirmedCountsWith` with no review cards in flight. */
export function ConfirmedCounts(tags: readonly ResearchCvTag[]): { confirmed: number; total: number } {
    return ConfirmedCountsWith(tags, []);
}

// ── Crumb ──────────────────────────────────────────────────────────────

const At = (view: CareerExplorerView): AGUCrumbItem['Location'] => ({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: view });
const IsReviewCategory = (c: string): c is ReviewCategory => (ReviewCategories as readonly string[]).includes(c);

/** The crumb trail (P/06_career-explorer.html:1579-1711); the last item is the current page, with no location. */
export function TrailFor(view: CareerExplorerView, roleTitle: string | null, category: string | null): AGUCrumbItem[] {
    switch (view) {
        case 'start':
        case 'ready':
            return [{ Label: 'Career Explorer', Location: null }];
        case 'paths':
            return [{ Label: 'Career Explorer', Location: At('ready') }, { Label: 'Career paths', Location: null }];
        case 'role':
            return [{ Label: 'Career paths', Location: At('paths') }, { Label: roleTitle ?? 'Role', Location: null }];
        case 'review': {
            const known = category !== null && IsReviewCategory(category) ? category : null;
            return known !== null
                ? [{ Label: 'Career Explorer', Location: At('ready') }, { Label: 'Review', Location: At('ready') }, { Label: ReviewCategoryNouns[known], Location: null }]
                : [{ Label: 'Career Explorer', Location: At('ready') }, { Label: 'Review', Location: null }];
        }
    }
}

// ── Role numbers ───────────────────────────────────────────────────────

export function PostingsOf(profile: ResearchJobProfile): number {
    return profile.searchTitles.reduce((sum, t) => sum + t.postings, 0);
}

/**
 * The skills figure both views print: how many of the role's skills the CV evidences, out of every skill row the role lists
 * (`skills[]`, which holds partial matches too, plus `notYetEvidenced[]`). P6:1516 and P6:1663 are the same quantity, so the card's
 * "{have} of {n} skills covered" and the role's "Skills {have} of {n}" read from this one function.
 */
export function SkillCoverage(match: ResearchRoleMatch): { have: number; total: number } {
    return { have: match.skills.filter(s => s.evidenced).length, total: match.skills.length + match.notYetEvidenced.length };
}

/** The names of the skills the member's CV evidences for this role. */
export function HeldSkillNames(match: ResearchRoleMatch): Set<string> {
    return new Set(match.skills.filter(s => s.evidenced).map(s => s.name));
}

export interface SeenTag {
    readonly providerTagID: string;
    readonly category: CvTagCategoryName;
}

/** Every requirement the matches mention, by name — what a member may add as their own. Client mirror of the server's SeenVocabulary; first mention wins. */
export function SeenVocabulary(matches: ResearchCareerMatches): ReadonlyMap<string, SeenTag> {
    const seen = new Map<string, SeenTag>();
    const add = (ref: ResearchRequirementRef | null, category: CvTagCategoryName): void => {
        if (ref && !seen.has(ref.name)) seen.set(ref.name, { providerTagID: ref.providerTagID, category });
    };
    const addMatched = (r: ResearchMatchedRequirement, category: CvTagCategoryName): void => { add(r, category); add(r.matchedVia, category); };
    for (const role of matches.matches) {
        for (const r of role.notYetEvidenced) add(r, 'skill');
        for (const r of role.blockingCredentials) add(r, 'certification');
        for (const r of role.skills) addMatched(r, 'skill');
        for (const r of role.education) add(r, 'education');
        for (const r of role.credentials) add(r, 'certification');
    }
    return seen;
}

/** The role card's stat tiles: median salary as `$82K` (null without salary data) and the most common education. */
export function RoleCardStats(profile: ResearchJobProfile): { salary: string | null; education: string | null } {
    const salary = profile.salary ? `$${Math.round(profile.salary.median / 1000)}K` : null;
    return { salary, education: profile.educationProfile.reduce<ResearchProfileShare | null>((top, s) => (top === null || s.share > top.share ? s : top), null)?.name ?? null };
}

export function TopRoles(matches: ResearchCareerMatches, n = 3): ResearchRoleMatch[] {
    return matches.matches.slice(0, n);
}

export function MoreRoles(matches: ResearchCareerMatches): ResearchRoleMatch[] {
    return matches.matches.filter(m => m.rank > 3);
}

// ── Ready view: Sharpen your results ───────────────────────────────────

/** One chip in the "Confirmed" box (P6:1625-1629): stated by the CV, approved in review, or added by the member (`Own`). */
export interface ConfirmedChip {
    readonly providerTagID: string;
    readonly Name: string;
    readonly Own: boolean;
    /** Approved in review (not stated, not the member's own): its `×` undoes the approval (P6:1630 `data-undo`). */
    readonly Approved: boolean;
    /** What an undo sends: the card's id and every chain member it stands for (as Decline does). */
    readonly UndoIds: readonly string[];
}

/** The review decision to read for a tag: an optimistic card decision beats the CV's own status until the server confirms it. */
function StatusOf(tag: ResearchCvTag, cardStatus: ReadonlyMap<string, ResearchCvReviewStatus>): ResearchCvReviewStatus {
    return cardStatus.get(tag.providerTagID) ?? tag.reviewStatus;
}

const IsConfirmedTag = (tag: ResearchCvTag, cardStatus: ReadonlyMap<string, ResearchCvReviewStatus>): boolean =>
    tag.confidence === 'stated' || tag.confidence === 'asserted' || StatusOf(tag, cardStatus) === 'approved';

const CardStatuses = (cards: readonly ReviewCard[]): ReadonlyMap<string, ResearchCvReviewStatus> =>
    new Map(cards.map(c => [c.providerTagID, c.reviewStatus] as const));

/** The confirmed tags of one category, the member's own first (latest first), then stated, then approved (P6:1620). Distinct by tag id. */
export function ConfirmedChips(tags: readonly ResearchCvTag[], cards: readonly ReviewCard[], category: ReviewCategory): ConfirmedChip[] {
    const status = CardStatuses(cards);
    const seen = new Set<string>();
    const own: ConfirmedChip[] = [];
    const stated: ConfirmedChip[] = [];
    const approved: ConfirmedChip[] = [];
    for (const t of tags) {
        if (t.category !== category || seen.has(t.providerTagID) || !IsConfirmedTag(t, status)) continue;
        seen.add(t.providerTagID);
        const isOwn = t.confidence === 'asserted';
        const isStated = t.confidence === 'stated';
        const card = cards.find(c => c.providerTagID === t.providerTagID);
        const chip: ConfirmedChip = {
            providerTagID: t.providerTagID, Name: t.name ?? t.providerTagID, Own: isOwn, Approved: !isOwn && !isStated,
            UndoIds: [t.providerTagID, ...(card?.chainMembers ?? [])],
        };
        (isOwn ? own : isStated ? stated : approved).push(chip);
    }
    return [...own.reverse(), ...stated, ...approved];
}

/** "{confirmed} of {total} tags confirmed" (P6:1531-1534), with approvals still in flight counted. */
export function ConfirmedCountsWith(tags: readonly ResearchCvTag[], cards: readonly ReviewCard[]): { confirmed: number; total: number } {
    const status = CardStatuses(cards);
    const all = new Set<string>();
    const confirmed = new Set<string>();
    for (const t of tags) {
        all.add(t.providerTagID);
        if (IsConfirmedTag(t, status)) confirmed.add(t.providerTagID);
    }
    return { confirmed: confirmed.size, total: all.size };
}

export interface TabRing {
    readonly Category: ReviewCategory;
    readonly Done: number;
    readonly Total: number;
    readonly Pending: number;
    /** 0..100; a tab with nothing to review is complete (P6:1540 `tot ? done/tot : 1`). */
    readonly Percent: number;
    readonly Complete: boolean;
}

/** The category tabs' mini rings: reviewed over total review cards (P6:1539-1542, 1608). */
export function TabRings(cards: readonly ReviewCard[]): TabRing[] {
    return ReviewCategories.map(category => {
        const inCategory = cards.filter(c => c.category === category);
        const done = inCategory.filter(c => c.reviewStatus !== 'unreviewed').length;
        const total = inCategory.length;
        const percent = total === 0 ? 100 : Math.round((done / total) * 100);
        return { Category: category, Done: done, Total: total, Pending: total - done, Percent: percent, Complete: percent >= 100 };
    });
}
